import { CardTypes, EventTypes, SubstitutionTypes } from "../../../types";
import type { GPEventResponse } from "../../../../../../../frontend/portal/src/libs/apiClient";
import { useContext, useEffect } from "react";
import { useVideoPlayer } from "../../../Contexts/contexts";
import { handlePlayEvent } from "../../../functions";
import clsx from "clsx";
import { useTimeStore } from "../../../time-store";

interface TimelineItemProps {
	event: GPEventResponse;
	homeTeamId: number;
	containerRef: React.RefObject<HTMLDivElement>;
	handleGetTimestamp: (event: GPEventResponse) => string | number | undefined;
}

export function TimelineItem({ event, homeTeamId, containerRef, handleGetTimestamp }: TimelineItemProps) {
	if (event.gameTeamId === null) {
		return null;
	}

	const { playerRef } = useVideoPlayer();
	const { currentTime } = useTimeStore();

	const ownTeamEvents = [EventTypes.Goal, EventTypes.Substitution, EventTypes.Card];
	const oppositeTeamEvents = [EventTypes.GoalOwn];

	const isHomeTeam = () => {
		return (
			(event.gameTeamId === homeTeamId && ownTeamEvents.includes(event.type as EventTypes)) ||
			(event.gameTeamId !== homeTeamId && oppositeTeamEvents.includes(event.type as EventTypes))
		);
	};

	const getItemIcon = () => {
		switch (event.type) {
			case EventTypes.Goal:
				return <span className="icon-new icon-soccer-ball icon-12" />;
			case EventTypes.GoalOwn:
				return <span className="icon-new icon-soccer-ball icon-12 red" />;
			case EventTypes.Card:
				return <div className={`card ${event.details?.card_type === CardTypes.Red ? "red" : "yellow"}`} />;
			case EventTypes.Substitution:
				return (
					<span className={`icon-new icon-12 ${event.details?.sub_type === SubstitutionTypes.On ? "icon-arrow-up" : "icon-arrow-down"}`} />
				);
		}
	};

	const isPlaying =
		playerRef?.current?.currentTime &&
		playerRef.current.currentTime * 1000 > event.videoTime - 5000 &&
		playerRef.current.currentTime * 1000 < event.videoTime + 5000;

	const isPassed = playerRef?.current?.currentTime && playerRef.current.currentTime * 1000 > event.videoTime - 5000;

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	// useEffect(() => {
	// 	if (
	// 		playerRef?.current?.currentTime
	// 		&& playerRef.current.currentTime * 1000 > event.videoTime - 5000
	// 		&& playerRef.current.currentTime * 1000 < event.videoTime + 5000
	// 	) {
	// 		setIsPlaying(true);
	// 	} else {
	// 		setIsPlaying(false);
	// 	}

	// 	if (
	// 		playerRef?.current?.currentTime
	// 		&& playerRef.current.currentTime * 1000 > event.videoTime - 5000
	// 	) {
	// 		setIsPassed(true);
	// 	} else {
	// 		setIsPassed(false);
	// 	}
	// }, [currentTime]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	useEffect(() => {
		const container = containerRef.current;
		if (container) {
			const activeElements = container.querySelectorAll(".timelineItem-container--passed");
			if (activeElements.length > 0) {
				const lastElement = activeElements[activeElements.length - 1] as HTMLElement;
				const containerRect = container.getBoundingClientRect();
				const elementRect = lastElement.getBoundingClientRect();

				const offsetTop = elementRect.top - containerRect.top;
				const newScrollTop = container.scrollTop + offsetTop - container.clientHeight / 2 + elementRect.height / 2;

				container.scrollTo({ top: newScrollTop, behavior: "smooth" });
			}
		}
	}, [isPlaying, isPassed]);

	return (
		<div
			className={clsx("timelineItem-container", {
				"timelineItem-container--home": isHomeTeam(),
				"timelineItem-container--away": !isHomeTeam(),
				"timelineItem-container--active": isPlaying,
				"timelineItem-container--passed": isPassed,
			})}
			data-time={`${event.videoTime}`}
			style={{ order: event.footballTime ?? event.videoTime }}
		>
			<div
				className="timelineItem"
				onClick={() => {
					handlePlayEvent(playerRef, event.videoTime - 5000);
				}}
			>
				<div className="iconContainer">{getItemIcon()}</div>
				<div className="timestamp">{`${handleGetTimestamp(event)}'`}</div>
				<div className="playerName">{event.playerName}</div>
				<span className="icon-new icon-play-event-white icon-14" />
			</div>
		</div>
	);
}
